import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { SidecarDetailRecord, SidecarOperatingSystem } from '../types/sidecar';

export type SidecarFormState = {
  name: string;
  operatingSystem: SidecarOperatingSystem;
  operatingSystemVersion: string;
  facilityId: string | undefined;
  discoveryEnabled: boolean;
};

const initialState = (): SidecarFormState => ({
  name: '',
  operatingSystem: 'Other' as SidecarOperatingSystem,
  operatingSystemVersion: '',
  facilityId: undefined,
  discoveryEnabled: true,
});

const mapSidecarToForm = (sidecar: SidecarDetailRecord): SidecarFormState => ({
  name: sidecar.name,
  operatingSystem: sidecar.operatingSystem,
  operatingSystemVersion: sidecar.operatingSystemVersion,
  facilityId: sidecar.facility?.id,
  discoveryEnabled: sidecar.settings.discoveryEnabled,
});

export const $sidecarForm = atom<SidecarFormState>(initialState());
export const $discardBaseline = atom<SidecarFormState | null>(null);
export const $lastPopulatedSidecarId = atom<string | undefined>(undefined);
export const $isSidecarFormPopulated = atom<boolean>(false);

export const $hasUnsavedChanges = atom((get) => {
  const form = get($sidecarForm);
  const baseline = get($discardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitSidecarForm = atom((get) => {
  const form = get($sidecarForm);
  if (!form.name.trim()) return false;
  if (!form.operatingSystem) return false;
  if (!form.operatingSystemVersion.trim()) return false;
  if (!form.facilityId) return false;
  return true;
});

export const $updateSidecarForm = atom(
  null,
  (get, set, update: Partial<SidecarFormState>) => {
    set($sidecarForm, { ...get($sidecarForm), ...update });
  },
);

export const $populateSidecarFormFromSidecar = atom(
  null,
  (_get, set, sidecar: SidecarDetailRecord) => {
    const formState = mapSidecarToForm(sidecar);
    set($sidecarForm, formState);
    set($discardBaseline, formState);
    set($lastPopulatedSidecarId, sidecar.id);
    set($isSidecarFormPopulated, true);
  },
);

export const $resetSidecarFormToSidecar = atom(null, (get, set) => {
  const baseline = get($discardBaseline);
  if (baseline) {
    set($sidecarForm, baseline);
  }
});

export const $populateSidecarFormAction = atom(
  null,
  (get, set, sidecar: SidecarDetailRecord | null | undefined) => {
    if (!sidecar) return;

    const currentSidecarId = sidecar.id;
    const lastSidecarId = get($lastPopulatedSidecarId);

    if (currentSidecarId !== lastSidecarId) {
      set($isSidecarFormPopulated, false);
    }

    if (!get($isSidecarFormPopulated)) {
      set($populateSidecarFormFromSidecar, sidecar);
    }
  },
);

export const $resetSidecarForm = atom(null, (_get, set) => {
  set($sidecarForm, initialState());
  set($discardBaseline, null);
  set($lastPopulatedSidecarId, undefined);
  set($isSidecarFormPopulated, false);
});
